import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';

const Website_status_maintenance = () => {
  const [websiteStatus, setWebsiteStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isToggling, setIsToggling] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({
    estimateTime: '00:00:00',
    maintenanceDetails: '',
    version: ''
  });

  useEffect(() => {
    fetchWebsiteStatus();
  }, []);

  const fetchWebsiteStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}api/Website/1`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch website status');
      }
      
      const data = await response.json();
      setWebsiteStatus(data);
      
      // Set form with existing data
      if (data.estimateTime) {
        setMaintenanceForm(prev => ({
          ...prev,
          estimateTime: data.estimateTime
        }));
      }
      if (data.mentenance_Details) {
        setMaintenanceForm(prev => ({
          ...prev,
          maintenanceDetails: data.mentenance_Details
        }));
      }
      if (data.version) {
        setMaintenanceForm(prev => ({
          ...prev,
          version: data.version
        }));
      }
      
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching website status:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async () => {
    try {
      setIsToggling(true);
      const newStatus = !websiteStatus.website_Status;
      
      const response = await fetch(`${API_BASE_URL}api/Website/1`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Id: 1,
          Website_Status: newStatus,
          EstimateTime: websiteStatus.estimateTime,
          Mentenance_Details: websiteStatus.mentenance_Details,
          Version: websiteStatus.version
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update website status');
      }

      const data = await response.json();
      setWebsiteStatus(data);
    } catch (err) {
      setError(err.message);
      console.error('Error updating website status:', err);
    } finally {
      setIsToggling(false);
    }
  };

  const updateMaintenanceDetails = async () => {
    try {
      const parseTimeSpan = (timeString) => {
        if (!timeString) return null;
        
        const timePattern = /^\d{1,2}:\d{2}:\d{2}$/;
        if (timePattern.test(timeString)) {
          return timeString;
        }
        
        return null;
      };

      const response = await fetch(`${API_BASE_URL}api/Website/1`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Id: 1,
          Website_Status: websiteStatus.website_Status,
          EstimateTime: parseTimeSpan(maintenanceForm.estimateTime),
          Mentenance_Details: maintenanceForm.maintenanceDetails || null,
          Version: maintenanceForm.version || null
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response:', errorText);
        throw new Error('Failed to update maintenance details');
      }

      const data = await response.json();
      setWebsiteStatus(data);
      setShowModal(false);
      alert('Maintenance details updated successfully!');
    } catch (err) {
      setError(err.message);
      console.error('Error updating maintenance details:', err);
      alert('Failed to update maintenance details');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Not set';
    
    const timeParts = timeString.split(':');
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0]);
      const minutes = parseInt(timeParts[1]);
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes} minutes`;
    }
    return timeString;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-20 h-20 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full border-2 border-red-200 shadow-xl">
          <div className="text-7xl mb-6 text-center">⚠️</div>
          <h3 className="text-3xl font-bold text-red-600 mb-4 text-center">Connection Error</h3>
          <p className="text-gray-600 text-center mb-8">{error}</p>
          <button 
            onClick={fetchWebsiteStatus}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            🔄 Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const isActive = websiteStatus?.website_Status;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        
        {/* Main Status Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-10 mb-6 border border-gray-100">
          
          {/* Status Indicator */}
          <div className="flex justify-center mb-8">
            <div className={`
              relative w-32 h-32 rounded-full flex items-center justify-center shadow-xl
              ${isActive 
                ? 'bg-gradient-to-br from-green-400 to-emerald-500' 
                : 'bg-gradient-to-br from-orange-400 to-red-500'}
              animate-pulse
            `}>
              <span className="text-6xl">{isActive ? '✅' : '🔧'}</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-5xl font-black text-gray-900 mb-6 text-center">
            Website Status
          </h1>

          {/* Status Badge */}
          <div className="flex justify-center mb-8">
            <div className={`
              px-8 py-3 rounded-full font-bold text-xl shadow-lg
              ${isActive 
                ? 'bg-green-100 text-green-800 border-2 border-green-300' 
                : 'bg-orange-100 text-orange-800 border-2 border-orange-300'}
            `}>
              {isActive ? '🟢 ONLINE' : '🔴 MAINTENANCE'}
            </div>
          </div>

          {/* Maintenance Details Section */}
          {!isActive && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-6 mb-8">
              <h3 className="text-xl font-bold text-orange-900 mb-4 flex items-center gap-2">
                🔧 Maintenance Information
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium">Estimated Duration:</span>
                  <span className="text-orange-700 font-bold">
                    {formatTime(websiteStatus?.estimateTime)}
                  </span>
                </div>
                
                {websiteStatus?.mentenance_Details && (
                  <div className="mt-4 pt-4 border-t border-orange-200">
                    <p className="text-gray-700 font-medium mb-2">Details:</p>
                    <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                      {websiteStatus.mentenance_Details}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          <p className="text-gray-600 text-xl text-center mb-10 leading-relaxed max-w-2xl mx-auto">
            {isActive 
              ? 'All systems operational. Website is running smoothly and accessible to all users.' 
              : 'Website is currently undergoing scheduled maintenance. We\'ll be back online shortly.'}
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-gray-50 rounded-2xl p-6 text-center border-2 border-gray-200 hover:border-blue-300 transition-all">
              <div className="text-4xl mb-3">⚡</div>
              <div className="text-gray-500 text-sm font-medium mb-1">Current Status</div>
              <div className={`font-bold text-lg ${isActive ? 'text-green-600' : 'text-orange-600'}`}>
                {isActive ? 'Active' : 'Maintenance'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6 text-center border-2 border-gray-200 hover:border-blue-300 transition-all">
              <div className="text-4xl mb-3">🕐</div>
              <div className="text-gray-500 text-sm font-medium mb-1">
                {isActive ? 'Uptime' : 'Est. Duration'}
              </div>
              <div className="text-gray-900 font-bold text-lg">
                {isActive ? '99.9%' : formatTime(websiteStatus?.estimateTime)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6 text-center border-2 border-gray-200 hover:border-blue-300 transition-all">
              <div className="text-4xl mb-3">👥</div>
              <div className="text-gray-500 text-sm font-medium mb-1">User Access</div>
              <div className="text-gray-900 font-bold text-lg">{isActive ? 'Available' : 'Limited'}</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            {/* Toggle Status Button */}
            <button 
              onClick={toggleStatus}
              disabled={isToggling}
              className={`
                w-full py-5 px-8 rounded-xl font-bold text-xl
                transition-all duration-300 transform shadow-lg
                ${isToggling 
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                  : isActive
                    ? 'bg-orange-600 hover:bg-orange-700 text-white hover:scale-105 active:scale-95'
                    : 'bg-green-600 hover:bg-green-700 text-white hover:scale-105 active:scale-95'}
                disabled:opacity-50
              `}
            >
              {isToggling ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-4 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                  Updating Status...
                </span>
              ) : (
                <span>
                  {isActive ? '🔴 Switch to Maintenance Mode' : '🟢 Activate Website'}
                </span>
              )}
            </button>

            {/* Edit Maintenance Details Button */}
            <button 
              onClick={() => setShowModal(true)}
              className="w-full py-4 px-8 rounded-xl font-bold text-lg
                        bg-blue-600 hover:bg-blue-700 text-white
                        transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg"
            >
              ⚙️ Edit Maintenance Details
            </button>
          </div>

          {/* Footer Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-2 text-gray-500 text-sm">
              <span>🕐 Last checked: {new Date().toLocaleTimeString()}</span>
              <span>📅 {new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* System Information Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-gray-900 font-bold text-xl mb-4 flex items-center gap-2">
            ℹ️ System Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Server Status:</span>
              <span className={`font-bold ${isActive ? 'text-green-600' : 'text-orange-600'}`}>
                {isActive ? '✓ Running' : '⏸ Maintenance'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">API Health:</span>
              <span className="text-green-600 font-bold">✓ Connected</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Response Time:</span>
              <span className="text-gray-900 font-bold">~45ms</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600 font-medium">Version:</span>
              <span className="text-gray-900 font-bold">{websiteStatus?.version || 'v2.1.0'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Maintenance Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              ⚙️ Edit Maintenance Details
            </h2>
            
            <div className="space-y-6">
              {/* Estimate Time Dropdown */}
              <div>
                <label className="block text-gray-700 font-bold mb-2">
                  Estimated Duration
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {/* Hours Dropdown */}
                  <div>
                    <label className="block text-gray-600 text-sm mb-2">Hours</label>
                    <select
                      value={maintenanceForm.estimateTime.split(':')[0] || '00'}
                      onChange={(e) => {
                        const hours = e.target.value;
                        const minutes = maintenanceForm.estimateTime.split(':')[1] || '00';
                        setMaintenanceForm({
                          ...maintenanceForm,
                          estimateTime: `${hours}:${minutes}:00`
                        });
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
                    >
                      {[...Array(25)].map((_, i) => (
                        <option key={i} value={String(i).padStart(2, '0')}>
                          {i} {i === 1 ? 'hour' : 'hours'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Minutes Dropdown */}
                  <div>
                    <label className="block text-gray-600 text-sm mb-2">Minutes</label>
                    <select
                      value={maintenanceForm.estimateTime.split(':')[1] || '00'}
                      onChange={(e) => {
                        const hours = maintenanceForm.estimateTime.split(':')[0] || '00';
                        const minutes = e.target.value;
                        setMaintenanceForm({
                          ...maintenanceForm,
                          estimateTime: `${hours}:${minutes}:00`
                        });
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
                    >
                      {[0, 15, 30, 45].map((min) => (
                        <option key={min} value={String(min).padStart(2, '0')}>
                          {min} minutes
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-gray-500 text-sm mt-2">
                  Select estimated maintenance duration
                </p>
              </div>

              {/* Maintenance Details Textarea */}
              <div>
                <label className="block text-gray-700 font-bold mb-2">
                  Maintenance Details
                </label>
                <textarea
                  rows="6"
                  placeholder="Enter maintenance details, reason, or any message for users..."
                  value={maintenanceForm.maintenanceDetails}
                  onChange={(e) => setMaintenanceForm({
                    ...maintenanceForm,
                    maintenanceDetails: e.target.value
                  })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-lg resize-none"
                />
              </div>

              {/* Version Dropdown */}
              <div>
                <label className="block text-gray-700 font-bold mb-2">
                  Version
                </label>
                <select
                  value={maintenanceForm.version || 'v1.0.0'}
                  onChange={(e) => setMaintenanceForm({
                    ...maintenanceForm,
                    version: e.target.value
                  })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
                >
                  {/* Generate versions from v1.0.0 to v20.0.0 */}
                  {[...Array(20)].map((_, major) => {
                    const versions = [];
                    // For each major version, create minor versions 0-9 and patch versions 0-9
                    for (let minor = 0; minor <= 9; minor++) {
                      for (let patch = 0; patch <= 9; patch++) {
                        const version = `v${major + 1}.${minor}.${patch}`;
                        versions.push(
                          <option key={version} value={version}>
                            {version}
                          </option>
                        );
                      }
                    }
                    return versions;
                  })}
                </select>
                <p className="text-gray-500 text-sm mt-2">
                  Current version: {websiteStatus?.version || 'Not set'}
                </p>
              </div>
            </div>

            {/* Modal Action Buttons */}
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-xl transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={updateMaintenanceDetails}
                className="flex-1 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Website_status_maintenance;